/**
 * 与えられた文字列が「同一オリジン内の安全なナビゲーション先パス」かどうかを判定する。
 *
 * react-router の `navigate()` / `<Navigate to>` に外部由来（URL クエリや API レスポンス）の
 * 値をそのまま渡すと、protocol-relative パス（`//evil.com`）や絶対 URL によって
 * オープンリダイレクトが成立しうる。本関数で rooted な内部パスのみを許可し、それ以外を弾く。
 *
 * - 許可: `/`, `/tenants`, `/tenants/xxx/image-library?foo=bar#baz`
 * - 拒否:
 *   - `//evil.com`（protocol-relative）
 *   - `/\evil.com`（バックスラッシュはブラウザで `/` に正規化されるため `//` と等価）
 *   - `/<TAB>/evil.com` 等、タブ・改行・CR を含む値（ブラウザの URL パーサが
 *     これらを除去してからパースするため、除去後に `//evil.com` へ化けて
 *     オープンリダイレクトになりうる。文字列前方一致だけでは防げないため拒否する）
 *   - `http://evil.com` / `https://evil.com`（絶対 URL）
 *   - `javascript:...` / `data:...`（スキーム）
 *   - rooted でない相対パス・空値
 */
export const isSafeInternalPath = (
  path: string | null | undefined
): path is string => {
  if (path == null || path === "") {
    return false;
  }

  // タブ(0x09)・改行(0x0A)・CR(0x0D) を含む値を拒否する。
  // これらはブラウザの URL パーサがパース前に除去するため、検証時の文字列と
  // 実際に解決される URL が乖離し、`/<TAB>/evil.com` → `//evil.com` のように
  // protocol-relative へ化けてガードをすり抜ける（オープンリダイレクト）。
  if (/[\t\n\r]/.test(path)) {
    return false;
  }

  // rooted パス（単一スラッシュ始まり）のみ許可し、絶対 URL・スキームを弾く
  if (!path.startsWith("/")) {
    return false;
  }

  // protocol-relative（`//` / `/\`）を弾く
  if (path.startsWith("//") || path.startsWith("/\\")) {
    return false;
  }

  return true;
};
